import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  Cog6ToothIcon, CheckIcon, ClipboardDocumentCheckIcon, ChevronRightIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

export default function SettingsPage() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings');
      setSettings(data);
      const d = {};
      for (const s of data) d[s.key] = JSON.stringify(s.value, null, 2);
      setDrafts(d);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (key) => {
    try {
      const value = JSON.parse(drafts[key]);
      await api.put(`/settings/${key}`, { value });
      toast.success('Saved');
      load();
    } catch (err) {
      toast.error(err instanceof SyntaxError ? 'Invalid JSON' : (err.response?.data?.error || 'Failed'));
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="animate-slide-up">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Cog6ToothIcon className="w-6 h-6 text-gray-500" /> Portal Settings
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Director-only. Edit values used in offer letters, disciplinary forms, and other generated documents.
        </p>
      </div>

      <Link to="/settings/onboarding-templates"
        className="block bg-white border border-gray-200 hover:border-orange-300 hover:bg-orange-50/30 rounded-2xl p-5 mb-3 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <ClipboardDocumentCheckIcon className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900">Onboarding Templates</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Control which onboarding items apply to which roles (cooks vs teachers vs admin).
            </p>
          </div>
          <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
        </div>
      </Link>

      <Link to="/settings/wallet"
        className="block bg-white border border-gray-200 hover:border-orange-300 hover:bg-orange-50/30 rounded-2xl p-5 mb-5 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <BanknotesIcon className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900">Points Wallet</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Hire bonus, cashout rate, infraction penalties, probation threshold — every number editable.
            </p>
          </div>
          <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
        </div>
      </Link>

      <div className="space-y-4">
        {settings.map(s => {
          const dirty = drafts[s.key] !== JSON.stringify(s.value, null, 2);
          return (
            <div key={s.key} className={`bg-white rounded-2xl border p-5 ${dirty ? 'border-orange-200' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-gray-900 font-mono text-sm">{s.key}</p>
                  {s.description && <p className="text-xs text-gray-500 mt-1">{s.description}</p>}
                </div>
                <button disabled={!dirty} onClick={() => save(s.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    dirty ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}>
                  <CheckIcon className="w-3.5 h-3.5 inline mr-1" /> Save
                </button>
              </div>
              <textarea value={drafts[s.key] || ''} onChange={(e) => setDrafts({ ...drafts, [s.key]: e.target.value })}
                rows={Math.min(12, (drafts[s.key] || '').split('\n').length + 1)}
                className="w-full mt-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-mono focus:border-orange-500 focus:outline-none resize-none" />
              {s.updated_at && (
                <p className="text-xs text-gray-400 mt-2">Last updated {new Date(s.updated_at).toLocaleString()}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
